import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ShopService } from '@app/services/shops/shop.service';
import { CurrencySign } from '@pdqdev/pdq-shared/dist/entities';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { SegmentedControlOption } from '@storybook/components/segmented-control/segmented-control.component';
import { finalize } from 'rxjs';
import {
  MaxxAlertChangeUnit,
  MaxxAlertConditionOperator,
  MaxxAlertMetric,
  MaxxMonitor,
  MaxxMonitorFrequency,
  MaxxMonitorInput,
} from '../maxx-settings.model';
import { MaxxSettingsService, maxxSettingsErrorMessage } from '../maxx-settings.service';

const OPERATOR_OPTIONS: DropdownOption[] = [
  { label: 'Drops by', value: 'drops_by' },
  { label: 'Rises by', value: 'rises_by' },
  { label: 'Is more than', value: 'is_more_than' },
  { label: 'Is less than', value: 'is_less_than' },
];

const FREQUENCY_OPTIONS: DropdownOption[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

const DAY_OF_WEEK_OPTIONS: DropdownOption[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
].map((label, value) => ({ label, value }));

const DAY_OF_MONTH_OPTIONS: DropdownOption[] = Array.from({ length: 28 }, (_, index) => ({
  label: String(index + 1),
  value: index + 1,
}));

const TIMEZONE_OPTIONS: DropdownOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

const DEFAULT_DAILY_THRESHOLDS: Record<MaxxAlertMetric, number> = {
  cvr: 15,
  aov: 15,
  arpc: 20,
  asr: 35,
};

const PERCENT_CHANGE_OPERATORS: MaxxAlertConditionOperator[] = ['drops_by', 'rises_by'];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function findOption(options: DropdownOption[], value: string | number): DropdownOption | undefined {
  return options.find((option) => option.value === value);
}

@Component({
  selector: 'maxx-monitor-form',
  templateUrl: './maxx-monitor-form.component.html',
  styleUrls: ['./maxx-monitor-form.component.scss'],
  standalone: false,
})
export class MaxxMonitorFormComponent implements OnInit {
  @Input({ required: true }) metric!: MaxxAlertMetric;
  @Input({ required: true }) title!: string;
  @Input() monitor: MaxxMonitor | null = null;
  @Input() slackChannelName: string | null = null;

  @Output() saved = new EventEmitter<MaxxMonitor>();
  @Output() closed = new EventEmitter<void>();

  readonly operatorOptions = OPERATOR_OPTIONS;
  readonly frequencyOptions = FREQUENCY_OPTIONS;
  readonly dayOfWeekOptions = DAY_OF_WEEK_OPTIONS;
  readonly dayOfMonthOptions = DAY_OF_MONTH_OPTIONS;
  readonly valueControl = new FormControl(10, { nonNullable: true });
  readonly newRecipientControl = new FormControl('', { nonNullable: true });
  readonly recipients = signal<string[]>([]);
  readonly postToSlack = signal(true);
  readonly recipientError = signal<string | null>(null);
  readonly operator = signal<MaxxAlertConditionOperator>('drops_by');
  readonly changeUnit = signal<MaxxAlertChangeUnit>('percent');
  readonly frequency = signal<MaxxMonitorFrequency>('daily');
  readonly dayOfWeek = signal(1);
  readonly dayOfMonth = signal(1);
  readonly timeOfDay = signal('09:00');
  readonly timezone = signal('America/New_York');
  readonly isSaving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly value = toSignal(this.valueControl.valueChanges, { initialValue: this.valueControl.value });
  readonly isPercentChange = computed(() => PERCENT_CHANGE_OPERATORS.includes(this.operator()));
  readonly showUnitToggle = computed(() => this.isPercentChange() && this.metric !== 'cvr');
  readonly valueSymbol = computed<CurrencySign | '%'>(() => {
    if (this.metric === 'cvr') return '%';
    if (this.isPercentChange() && this.changeUnit() === 'percent') return '%';
    return this.currencySymbol;
  });
  readonly unitOptions = computed<SegmentedControlOption[]>(() => [
    { label: '%', value: 'percent' },
    { label: this.currencySymbol, value: 'amount' },
  ]);
  readonly selectedOperator = computed(() => findOption(OPERATOR_OPTIONS, this.operator()));
  readonly selectedFrequency = computed(() => findOption(FREQUENCY_OPTIONS, this.frequency()));
  readonly selectedDayOfWeek = computed(() => findOption(DAY_OF_WEEK_OPTIONS, this.dayOfWeek()));
  readonly selectedDayOfMonth = computed(() => findOption(DAY_OF_MONTH_OPTIONS, this.dayOfMonth()));
  readonly timezoneOptions = computed(() =>
    findOption(TIMEZONE_OPTIONS, this.timezone())
      ? TIMEZONE_OPTIONS
      : [...TIMEZONE_OPTIONS, { label: this.timezone(), value: this.timezone() }],
  );
  readonly selectedTimezone = computed(() => findOption(this.timezoneOptions(), this.timezone()));
  readonly canSave = computed(() => this.value() > 0 && this.recipients().length > 0);

  private readonly maxxSettingsService = inject(MaxxSettingsService);
  private readonly shopService = inject(ShopService);
  private readonly currencySymbol: CurrencySign = this.shopService.shopCurrencySymbol ?? '$';
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const monitor = this.monitor;
    const condition = monitor?.alertCondition;
    if (condition) {
      this.operator.set(condition.operator);
      this.changeUnit.set(condition.changeUnit ?? 'percent');
      this.valueControl.setValue(condition.value);
    } else {
      this.valueControl.setValue(DEFAULT_DAILY_THRESHOLDS[this.metric]);
    }
    if (monitor) {
      this.frequency.set(monitor.frequency);
      this.dayOfWeek.set(monitor.dayOfWeek ?? 1);
      this.dayOfMonth.set(monitor.dayOfMonth ?? 1);
      this.timeOfDay.set(monitor.timeOfDay);
      this.timezone.set(monitor.timezone);
    }
    const shopEmail: string | undefined = this.shopService.currentShopValue?.metadata?.email;
    this.recipients.set(monitor?.recipients ?? (shopEmail ? [shopEmail.toLowerCase()] : []));
    this.postToSlack.set(monitor?.postToSlack ?? true);
  }

  select<T>(target: WritableSignal<T>, option: DropdownOption): void {
    target.set(option.value as T);
  }

  setChangeUnit(unit: string): void {
    this.changeUnit.set(unit as MaxxAlertChangeUnit);
  }

  setTimeOfDay(event: Event): void {
    const time = (event.target as HTMLInputElement).value;
    if (time) this.timeOfDay.set(time);
  }

  addRecipient(): void {
    const email = (this.newRecipientControl.value ?? '').trim().toLowerCase();
    if (!email) return;
    if (!EMAIL.test(email)) {
      this.recipientError.set('Please enter a valid email address');
      return;
    }
    if (this.recipients().includes(email)) {
      this.recipientError.set('This email is already added');
      return;
    }
    this.recipients.update((recipients) => [...recipients, email]);
    this.newRecipientControl.setValue('');
    this.recipientError.set(null);
  }

  removeRecipient(email: string): void {
    this.recipients.update((recipients) => recipients.filter((item) => item !== email));
  }

  save(): void {
    if (!this.canSave() || this.isSaving()) return;

    const changes: Omit<MaxxMonitorInput, 'name' | 'metric'> = {
      alertCondition: {
        operator: this.operator(),
        value: this.value(),
        ...(this.isPercentChange() && { changeUnit: this.metric === 'cvr' ? 'percent' : this.changeUnit() }),
      },
      frequency: this.frequency(),
      dayOfWeek: this.frequency() === 'weekly' ? this.dayOfWeek() : undefined,
      dayOfMonth: this.frequency() === 'monthly' ? this.dayOfMonth() : undefined,
      timeOfDay: this.timeOfDay(),
      timezone: this.timezone(),
      recipients: this.recipients(),
      postToSlack: this.postToSlack(),
    };
    const request = this.monitor
      ? this.maxxSettingsService.updateMonitor(this.monitor.id, changes)
      : this.maxxSettingsService.createMonitor({ ...changes, name: this.title, metric: this.metric });

    this.isSaving.set(true);
    this.errorMessage.set(null);
    request
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (monitor) => this.saved.emit(monitor),
        error: (error) => this.errorMessage.set(maxxSettingsErrorMessage(error)),
      });
  }
}
